import { faCalendarLines } from '@fortawesome/pro-regular-svg-icons/faCalendarLines';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { AppImageDimension } from 'app/appImages';
import { MprcButton, MprcCardActions, MprcRequestIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { ManagedEnrollmentClassroomListResponse, ManageEnrollOperation, TrainingDueDateOption } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import TzUtils from 'lib/TzUtils';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { LmsTrainingDefaultThumbnailUrl } from "views/lms/components/LmsTypeImage";
import { EditEnrollmentStepProps } from "views/lms/enrollment/Home";
import { useEnrollmentManagementContext } from "views/lms/enrollment/Home/EnrollmentManagementWrapper";

export const ConfirmBox: React.FC<EditEnrollmentStepProps> = (props) => {

    const ctx = useEnrollmentManagementContext();
    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const training = ctx.training!;

    const [posting, setPosting] = useState<boolean>(false);

    ///
    const buildFirstLine = (item: ManagedEnrollmentClassroomListResponse): React.ReactNode => {
        if (item.executionStartDate != null && item.executionDueDate != null && item.executionStartDate == item.executionDueDate) {
            return <Typography variant="caption" color="muted.main">
                {
                    t("Enrollment.StepTraining.ListTraining.DateText", {
                        name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionStartDate, item.timezoneId)!, false)
                    })
                }
            </Typography>;
        } else if (item.executionStartDate == null) {
            return <Typography variant="caption" color="muted.main">{t("Enrollment.StepTraining.ListTraining.AnyTimeText")}</Typography>;
        } else if (item.executionStartDate != null) {
            return <Typography variant="caption" color="muted.main">
                {
                    t("Enrollment.StepTraining.ListTraining.StartDateText", {
                        name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionStartDate, item.timezoneId)!, false)
                    })
                }
            </Typography>;
        }

        return <Box></Box>;
    };

    ///
    const buildSecondLine = (item: ManagedEnrollmentClassroomListResponse): React.ReactNode => {

        if (!item.enrollSuspended) {
            if (item.executionDueDateOption == TrainingDueDateOption.UntilDaysAfterStartTraining && item.executionDueDays) {
                return <Typography variant="caption" color="muted.main">
                    {t("Enrollment.StepTraining.ListTraining.UntilDaysAfterStartText", { count: item.executionDueDays })}
                </Typography>;
            } else if (item.executionDueDateOption == TrainingDueDateOption.UntilSpecifedDate && item.executionDueDate && item.executionStartDate != item.executionDueDate) {
                return <Typography variant="caption" color="muted.main">
                    {
                        t("Enrollment.StepTraining.ListTraining.DueDateText", {
                            name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionDueDate, item.timezoneId)!, false)
                        })
                    }
                </Typography>;
            }

            if (item.availableEnrollmentsCount > 0) {
                return <Typography variant="caption" color="muted.main">
                    {t("Enrollment.StepTraining.ListTraining.AvailableEnrollmentText", { count: item.availableEnrollmentsCount })}
                </Typography>;
            }
        }

        return <Box></Box>;
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.enrollmentOperation.processEnroll({
                    trainingId: training.trainingId,
                    operation: ctx.operation!
                });
                if (result) {
                    ctx.setResult(result.data);
                    if (result.data) props.goNextStep();
                }
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setPosting(false);
            }
        }

        if (posting) postData();

    }, [posting]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={posting} type="post" />

            <Stack alignContent="center" alignItems="center" mt={4}>
                <Card sx={{ minWidth: "650px" }}>
                    <CardContent>
                        <Typography variant="h2" color="muted">{training.name}</Typography>
                        <Grid container marginTop={1} columnSpacing={2}>
                            <Grid>
                                <Avatar
                                    variant="rounded"
                                    sx={{ ...AppImageDimension.ThumbnailLg }}
                                    src={LmsTrainingDefaultThumbnailUrl(training.contentType, training.thumbnailUrl)}
                                />
                            </Grid>
                            <Grid size="grow" marginTop={1}>
                                <Grid container direction="column" alignContent="start" spacing={1}>
                                    {ctx.classroom &&
                                        <>
                                            <Grid>
                                                <Typography variant="h4" color="muted.main">
                                                    {training.multipleClasses ? ctx.classroom.name : ""}
                                                </Typography>
                                            </Grid>
                                            <Grid>
                                                <Stack direction="row" spacing={1}>
                                                    <FontAwesomeIcon icon={faCalendarLines} size="3x" />
                                                    <Stack direction="column" marginTop={1}>
                                                        {buildFirstLine(ctx.classroom)}
                                                        {buildSecondLine(ctx.classroom)}
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                        </>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <MprcCardActions>
                        <MprcButton
                            variant="contained"
                            color="success"
                            onClick={() => setPosting(true)}
                            text={t(ctx.operation == ManageEnrollOperation.Enrollment
                                ? "Enrollment.StepConfirm.EnrollmentButtonText"
                                : ctx.operation == ManageEnrollOperation.Cancel
                                    ? "Enrollment.StepConfirm.CanceButtonText"
                                    : ctx.operation == ManageEnrollOperation.AllowExtraTime
                                        ? "Enrollment.StepConfirm.ExtraTimeButtonText"
                                        : ctx.operation == ManageEnrollOperation.Revoke
                                            ? "Enrollment.StepConfirm.RevokeButtonText"
                                            : ctx.operation == ManageEnrollOperation.UndoRevoke
                                                ? "Enrollment.StepConfirm.UndoRevokeButtonText"
                                                : ctx.operation == ManageEnrollOperation.ReEnrollForRecycling
                                                    ? "Enrollment.StepConfirm.ReEnrollButtonTitle"
                                                    : ctx.operation == ManageEnrollOperation.Exclude
                                                        ? "Enrollment.StepConfirm.ExcludeButtonText"
                                                        : "Enrollment.StepConfirm.FinalizeButtonText"
                                , { count: training.totalSelected })}
                            title={t(ctx.operation == ManageEnrollOperation.Enrollment
                                ? "Enrollment.StepConfirm.EnrollmentButtonTitle"
                                : ctx.operation == ManageEnrollOperation.Cancel
                                    ? "Enrollment.StepConfirm.CanceButtonTitle"
                                    : ctx.operation == ManageEnrollOperation.AllowExtraTime
                                        ? "Enrollment.StepConfirm.ExtraTimeButtonTitle"
                                        : ctx.operation == ManageEnrollOperation.Revoke
                                            ? "Enrollment.StepConfirm.RevokeButtonTitle"
                                            : ctx.operation == ManageEnrollOperation.UndoRevoke
                                                ? "Enrollment.StepConfirm.UndoRevokeButtonTitle"
                                                : ctx.operation == ManageEnrollOperation.ReEnrollForRecycling
                                                    ? "Enrollment.StepConfirm.ReEnrollButtonTitle"
                                                    : ctx.operation == ManageEnrollOperation.Exclude
                                                        ? "Enrollment.StepConfirm.ExcludeButtonTitle"
                                                        : "Enrollment.StepConfirm.FinalizeButtonTitle"
                                , { count: training.totalSelected })}
                        />
                    </MprcCardActions>
                </Card>
            </Stack>
        </>
    );
};
